import { useState, useEffect, useCallback, useMemo } from 'react';
import { getIncome } from '@/lib/api';
import { IncomeKPICards } from '@/components/income/IncomeKPICards';
import { IncomeMonthRow } from '@/components/income/IncomeMonthRow';
import IncomeByUnitView from '@/components/income/IncomeByUnitView';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ChevronLeft, ChevronRight, Calendar, Grid3x3 } from 'lucide-react';
import { toast } from 'sonner';

export default function IncomePage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { setData(await getIncome(year)); }
    catch { toast.error('Failed to load income data'); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  // Peak month total (for the trend-bar scaling in IncomeMonthRow)
  const peakTotal = useMemo(() => {
    if (!data) return 0;
    return data.months.reduce((m, x) => Math.max(m, x.total || 0), 0);
  }, [data]);

  return (
    <div className="space-y-6" data-testid="rent-roll-page">
      {/* Corporate header strip */}
      <div className="flex items-end justify-between border-b border-slate-200 pb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">Financial Report</p>
          <h1 className="font-heading text-[28px] font-semibold tracking-tight text-slate-900">Rent Roll</h1>
          <p className="text-xs text-slate-500 mt-1">Rental income across all properties · compiled from tenant leases &amp; miscellaneous charges.</p>
        </div>
        <div className="flex items-center gap-1.5 border border-slate-200 rounded-md bg-white shadow-sm px-1 py-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-slate-100" onClick={() => setYear((y) => y - 1)} data-testid="income-year-prev">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="font-heading text-sm font-semibold tabular-nums w-14 text-center text-slate-900">FY {year}</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-slate-100" onClick={() => setYear((y) => y + 1)} data-testid="income-year-next">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Outer tabs: By Month | By Unit */}
      <Tabs defaultValue="by-month" className="space-y-5">
        <TabsList className="bg-white border border-slate-200 p-1">
          <TabsTrigger value="by-month" className="text-xs gap-1.5 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="rr-tab-by-month">
            <Calendar className="h-3.5 w-3.5" /> By Month
          </TabsTrigger>
          <TabsTrigger value="by-unit" className="text-xs gap-1.5 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="rr-tab-by-unit">
            <Grid3x3 className="h-3.5 w-3.5" /> By Unit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="by-month" className="space-y-5">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-500 text-sm">Loading…</div>
          ) : data ? (
            <>
              <IncomeKPICards data={data} year={year} />
              <div className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between">
                  <h2 className="text-[11px] uppercase tracking-wider font-semibold text-slate-700">Monthly Breakdown</h2>
                  <span className="text-[11px] text-slate-500 tabular-nums">{data.months.filter((m) => m.total > 0).length} active · ${(data.yearly_total || 0).toLocaleString()} total</span>
                </div>
                {data.months.map((m) => (
                  <IncomeMonthRow
                    key={m.month}
                    month={m}
                    year={year}
                    isCurrentMonth={m.month === currentMonth && year === currentYear}
                    highlightTotal={peakTotal}
                  />
                ))}
              </div>
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="by-unit">
          <IncomeByUnitView />
        </TabsContent>
      </Tabs>
    </div>
  );
}
